import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FlaskConical, HelpCircle, Store } from 'lucide-react';
import { npcShopPath, priceLabel, sellersOf } from '../../utils/npcShops';
import { WorldSection } from './WorldPageHeader';
import { SourceCard } from './SourceCard';
import { BagSourceList } from './BagSourceList';
import { DataStatus, missingFiles } from './DataStatus';
import { groupLabel, playerItemSources, qualityLabel, routeQuality, sourceGroup } from '../../utils/itemSources';
import type { Quality, ResolvedSource, SourceGroup } from '../../utils/itemSources';
import { isOpenableBag, playerBagSources } from '../../utils/bagSources';
import type { BagSource } from '../../utils/bagSources';
import { recipesProducing } from '../../utils/recipeLinks';
import type { WorldData } from '../../types/world';
const GROUPS: SourceGroup[] = ['drop', 'event', 'box', 'quest', 'other'];
const QUALITIES: Quality[] = ['normal', 'excellent', 'ancient', 'other'];

/**
 * "Dónde conseguirlo": todas las formas de obtención documentadas, evaluadas ruta por ruta y
 * separadas por tipo (drop, invasión/evento, caja, misión) y calidad (normal, Excellent, Ancient).
 * Las asociaciones retenidas por conflicto no aparecen; las rutas desactivadas van al final.
 */
export function WhereToGet({
  w,
  id,
  name
}: {
  w: WorldData;
  id: string;
  name: string;
}) {
  const all = useMemo(() => playerItemSources(w, id), [w, id]);
  const active = all.filter(s => s.status !== 'disabled');
  const disabled = all.filter(s => s.status === 'disabled');
  const bags = useMemo(() => {
    const viaRoutes = new Set(active.map(s => s.route.bagId).filter(Boolean));
    return playerBagSources(w, id).filter(b => !viaRoutes.has(b.bag.id));
  }, [w, id, active]);
  const crafted = useMemo(() => recipesProducing(id, name), [id, name]);
  const sellers = useMemo(() => sellersOf(id), [id]);
  const [showDisabled, setShowDisabled] = useState(false);
  const missingRoutes = missingFiles(w, ['routes', 'routeTargets']);
  const missingBags = missingFiles(w, ['bags', 'bagTriggers', 'bagRewards']);
  const grouped = GROUPS.map(g => ({
    g,
    sources: active.filter(s => sourceGroup(s) === g),
    bags: g === 'box' ? bags.filter(isOpenableBag) : g === 'event' ? bags.filter(b => !isOpenableBag(b)) : [] as BagSource[]
  })).filter(x => x.sources.length || x.bags.length);
  const located = active.some(s => s.eventMaps.length || s.eventMapId !== undefined || s.targets.some(t => t.spawns.length));
  const documented = active.some(s => !s.pending) || bags.length > 0;
  const total = active.length + bags.length;
  return <WorldSection id="donde-conseguirlo" title="Dónde conseguirlo" count={total + crafted.length + sellers.length} data-id="element-1843" data-mp="2zxbsw41dfnw4">
      <div className="-mt-1 mb-3 flex flex-wrap gap-1.5" data-id="element-1844" data-mp="34rocce2ukz7v">
        {missingRoutes.length ? <DataStatus kind="unchecked" data-id="element-1845" data-mp="279xt7y6age8b" /> : <>
            {documented && <DataStatus kind="route" data-id="element-1846" data-mp="2c7puvjwqjepp" />}
            {located && <DataStatus kind="location" withDate={false} data-id="element-1847" data-mp="24pj8vomcf0bt" />}
            {active.some(s => s.pending) && <DataStatus kind="partial" data-id="element-1848" data-mp="1zqy8np73cgnd" />}
          </>}
      </div>

      {missingRoutes.length > 0 && <p className="mb-3 flex items-start gap-2 rounded-xl bg-white/[0.03] px-3 py-2.5 text-sm text-gray-300" data-id="element-1849" data-mp="1ur2cud8jrviw">
          <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" data-id="element-1850" data-mp="1qoyqgtkcb6iu" />
          No comprobado: no se pudieron cargar las rutas de obtención. Esto no significa que el ítem no tenga drops.
        </p>}

      {!missingRoutes.length && total === 0 && crafted.length === 0 && sellers.length === 0 && <p className="text-gray-400" data-id="element-1851" data-mp="1c1do51vahbbz">{disabled.length ? 'Ninguna forma de obtención está disponible en la configuración actual.' : 'Ubicación de obtención pendiente de confirmar.'}</p>}

      <div className="space-y-6" data-id="element-1852" data-mp="12zwcbvowzvt3">
        {grouped.map(({
        g,
        sources,
        bags: gb
      }) => {
        const qs = QUALITIES.filter(q => sources.some(s => routeQuality(s.route) === q));
        return <div key={g} data-id="element-1853" data-mp="2y4duepb7gc7u">
              <h3 className="mb-2.5 font-cinzel text-sm md:text-base font-bold text-[#D4AF37]" data-id="element-1854" data-mp="2y5yfzpfjkktk">{groupLabel[g]}</h3>
              {qs.map(q => <div key={q} className="mb-3 last:mb-0" data-id="element-1855" data-mp="2ocptzywo7ku8">
                  {(qs.length > 1 || q !== 'normal') && <p className="mb-2 text-xs font-semibold text-gray-300" data-id="element-1856" data-mp="2tbu6ayew5yos">{qualityLabel[q]}</p>}
                  <ul className="grid gap-3" data-id="element-1857" data-mp="2eejvlra2owu4">
                    {sources.filter(s => routeQuality(s.route) === q).map(s => <SourceCard key={s.key} w={w} s={s} data-id="element-1858" data-mp="2jfhe64yzvsw5" />)}
                  </ul>
                </div>)}
              {gb.length > 0 && <div className={sources.length ? 'mt-3' : ''} data-id="element-1859" data-mp="2adzwii5xvj5c">
                  {missingBags.length > 0 ? <DataStatus kind="unchecked" data-id="element-1860" data-mp="3nk2ji4dzfmy" /> : <BagSourceList w={w} sources={gb} data-id="element-1861" data-mp="3nacjujcd8h9" />}
                </div>}
            </div>;
      })}

        {sellers.length > 0 && <div data-id="element-1862" data-mp="8kaepud2lr33">
            <h3 className="mb-2.5 font-cinzel text-sm md:text-base font-bold text-[#D4AF37]" data-id="element-1863" data-mp="iqbp4dcv6r4r">Comprar a un NPC</h3>
            <ul className="grid gap-2" data-id="element-1864" data-mp="ds9x32xgnhh5">
              {sellers.map(({
            npc,
            item
          }) => <li key={`${npc.id}-${item.itemId}-${item.level ?? ''}`} data-id="element-1865" data-mp="rjf26kjll1yv">
                  <Link to={npcShopPath(npc.id)} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0A1628] p-3.5 transition-colors duration-150 hover:border-[#D4AF37]/40" data-id="element-1866" data-mp="ms6qtiypj6j2">
                    <Store className="h-5 w-5 shrink-0 text-[#D4AF37]" aria-hidden="true" data-id="element-1867" data-mp="11iw20lc64lmx" />
                    <span className="min-w-0" data-id="element-1868" data-mp="wkcggm1hlbv3">
                      <span className="block font-semibold text-white" data-id="element-1869" data-mp="1agpypxduhham">{npc.name}</span>
                      <span className="block text-xs text-gray-400" data-id="element-1870" data-mp="16fwb754be4i9">
                        {[item.level !== undefined ? `+${item.level}` : '', priceLabel(item) ?? 'Precio no documentado', 'Ver ubicación del vendedor'].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                  </Link>
                </li>)}
            </ul>
          </div>}

        {crafted.length > 0 && <div data-id="element-1871" data-mp="1g68238pejas7">
            <h3 className="mb-2.5 font-cinzel text-sm md:text-base font-bold text-[#D4AF37]" data-id="element-1872" data-mp="1g82efymlxuko">Fabricación</h3>
            {total === 0 && <p className="mb-2 text-sm text-gray-300" data-id="element-1873" data-mp="1q3vz65ahcudj">No tiene drops documentados: se obtiene combinando materiales.</p>}
            <ul className="grid gap-2" data-id="element-1874" data-mp="1u6iskamsuncu">
              {crafted.map(r => <li key={r.id} data-id="element-1875" data-mp="1z4kd6zzviuqf">
                  <a href="#como-crearlo" className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0A1628] p-3.5 transition-colors duration-150 hover:border-[#D4AF37]/40" data-id="element-1876" data-mp="1z3j0kr37m06l">
                    <FlaskConical className="h-5 w-5 shrink-0 text-[#D4AF37]" aria-hidden="true" data-id="element-1877" data-mp="28tlpaf3asrz4" />
                    <span className="min-w-0" data-id="element-1878" data-mp="2durlwo02wzdx">
                      <span className="block font-semibold text-white" data-id="element-1879" data-mp="2drwziqxi94hv">{r.title} · {r.machine ?? 'Chaos Machine'}</span>
                      <span className="block text-xs text-gray-400" data-id="element-1880" data-mp="67q6vqwwx841">Ver materiales y pasos más abajo · receta clásica, pendiente de validar en Vanguard</span>
                    </span>
                  </a>
                </li>)}
            </ul>
          </div>}

        {disabled.length > 0 && <div data-id="element-1881" data-mp="3oeeafccbdn7l">
            <button type="button" onClick={() => setShowDisabled(v => !v)} aria-expanded={showDisabled} className="h-10 text-sm font-semibold text-gray-400 hover:text-white" data-id="element-1882" data-mp="3oeweuw94r7ql">
              {showDisabled ? 'Ocultar' : 'Ver'} formas no disponibles ahora ({disabled.length})
            </button>
            {showDisabled && <ul className="mt-2 grid gap-3" data-id="element-1883" data-mp="g4l1vztz8qdk">{disabled.map((s: ResolvedSource) => <SourceCard key={s.key} w={w} s={s} data-id="element-1884" data-mp="b7uoxldgyawd" />)}</ul>}
          </div>}
      </div>
    </WorldSection>;
}